import { EXCLUDED_SUB_KEYS, SPECIAL_KEYS } from "./constants";
import {
  appendDefinitionList,
  capitalizeFirstLetter,
  createElement,
  reformatIfDateString,
  transformString,
} from "./dom";

export default class AnswerFormatter {
  constructor(survey) {
    this.survey = survey;
  }

  render(targetDiv, question, subQuestionName = null) {
    const surveyValue = this.survey.getValue(question.name);
    if (surveyValue === undefined || surveyValue === null) {
      return null;
    }

    const questionName = subQuestionName || question.name;
    const questionModel = this.survey.getQuestionByName(questionName);
    const choiceSource = questionModel || question;

    let answerText =
      subQuestionName !== null && subQuestionName !== undefined
        ? surveyValue?.[subQuestionName]
        : surveyValue;

    if (isValueEmpty(answerText)) {
      return null;
    }

    if (Array.isArray(surveyValue) && !(typeof surveyValue[0] === "string")) {
      answerText = surveyValue.map((item) => item[subQuestionName]);

      if (!answerText || answerText.every((value) => isValueEmpty(value))) {
        return null;
      }
    }

    if (Array.isArray(answerText) && typeof answerText[0] === "string") {
      const mappedAnswers = mapArrayValues(choiceSource, answerText);
      if (mappedAnswers.length === 0) {
        return null;
      }
      const joined = mappedAnswers.join(", ");
      const span = createElement("span");
      span.innerText = capitalizeFirstLetter(joined.trim());
      targetDiv.appendChild(span);
      return joined;
    }

    if (isPlainChoiceObject(answerText)) {
      answerText = answerText.text || answerText.title || answerText.value;
    } else if (answerText && typeof answerText === "object") {
      const handledAnswer = this.handleObjects(targetDiv, answerText, question, subQuestionName, choiceSource);
      if (handledAnswer === null) {
        return null;
      }
      if (handledAnswer === "") {
        return "";
      }
      answerText = handledAnswer;
    } else {
      answerText = formatPrimitiveDisplayValue(choiceSource, answerText);
    }

    if (isValueEmpty(answerText)) {
      return null;
    }

    if (typeof answerText === "string" && !answerText.includes("@")) {
      answerText = answerText
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }

    const answerElement = createElement("span");
    answerElement.innerText = reformatIfDateString(answerText);
    targetDiv.appendChild(answerElement);

    return answerText;
  }

  handleObjects(div, answerObject, answerValue, subAnswerValue, choiceSource = null) {
    if (Array.isArray(answerObject)) {
      if (answerObject.length === 1 && answerObject[0] && Object.keys(answerObject[0]).length === 0) {
        return null;
      }
    }

    const container = createElement("div");
    const questionType = choiceSource?.getType?.() || answerValue?.getType?.();

    if (SPECIAL_KEYS.RELATIONSHIPS.includes(answerValue.name)) {
      this.getRelationDetails(div, container, answerObject, answerValue, subAnswerValue);
      return "";
    }

    if (SPECIAL_KEYS.TABLES.includes(answerValue.name)) {
      this.getTableDetails(div, container, answerObject, answerValue);
      return "";
    }

    if (["matrixdropdown", "matrixdynamic"].includes(questionType)) {
      this.handleMatrixDropdownAnswers(div, container, choiceSource, answerObject);
      return "";
    }

    if (isPlainChoiceObject(answerObject)) {
      return answerObject.text || answerObject.title || answerObject.value;
    }

    if (!Array.isArray(answerObject) && typeof answerObject === "object") {
      const details = Object.entries(answerObject)
        .filter(([, value]) => !isValueEmpty(value))
        .map(([key, value]) => {
          const displayValue = formatPrimitiveDisplayValue(choiceSource, value);
          return `${transformString(key)}: ${displayValue}`;
        });

      if (details.length > 0) {
        appendDefinitionList(container, div, details);
        return "";
      }
      return null;
    }

    return answerObject;
  }

  getRelationDetails(div, container, relationsArray, templateData, templateName) {
    if (typeof relationsArray[0] === "string") {
      appendDefinitionList(container, div, relationsArray);
    } else {
      for (const relationData of relationsArray) {
        const relationDetails = [];

        for (const relationKey in relationData) {
          if (EXCLUDED_SUB_KEYS.includes(relationKey)) continue;

          const templateItems =
            templateData.templateElements.find((element) => element.name === templateName)?.items ||
            [];
          let itemTitle = templateItems.find((it) => it.name === relationKey)?.title;

          if (!itemTitle) {
            const element = templateData.templateElements.find((template) => template.name === relationKey);
            itemTitle = element?.title;
          }

          if (!itemTitle) continue;

          const rawValue = relationData[relationKey];
          const displayValue = reformatIfDateString(rawValue);
          relationDetails.push(`${itemTitle}: ${displayValue}`);
        }

        appendDefinitionList(container, div, relationDetails, transformString(templateName));
      }
    }
  }

  getTableDetails(div, container, answerObject, answerValue) {
    if (typeof answerObject[0] === "string") {
      appendDefinitionList(container, div, answerObject);
    } else {
      for (const row in answerObject) {
        const rowObject = answerValue.rows.find((item) => item.value === row);
        const details = [];
        const displayValue = answerObject[row].boolean ? "Yes" : "No";
        details.push(`${rowObject.text}: ${displayValue}`);

        appendDefinitionList(container, div, details);
      }
    }
  }

  handleMatrixDropdownAnswers(div, container, question, answerObject) {
    if (!question || !answerObject) {
      return;
    }

    const details = Object.entries(answerObject).map(([rowKey, rowValue]) => {
      const rowDefinition = question.rows?.find((row) => row.value === rowKey);
      const rowLabel = rowDefinition?.text || transformString(rowKey);

      if (!rowLabel) {
        return null;
      }

      let displayValue;

      if (typeof rowValue === "object" && rowValue !== null && !Array.isArray(rowValue)) {
        const columnDetails = Object.entries(rowValue).map(([columnKey, columnValue]) => {
          const columnDefinition =
            question.columns?.find((column) => column.name === columnKey || column.value === columnKey) ||
            {};
          const formattedValue = formatPrimitiveDisplayValue(columnDefinition, columnValue)
            || formatPrimitiveDisplayValue(question, columnValue);

          if (Object.keys(rowValue).length > 1) {
            const columnLabel =
              columnDefinition.title || columnDefinition.text || transformString(columnKey);
            return `${columnLabel} ${formattedValue}`;
          }

          return formattedValue;
        });

        displayValue = columnDetails.join(", ");
      } else {
        displayValue = formatPrimitiveDisplayValue(question, rowValue);
      }

      return displayValue ? `${rowLabel}: ${displayValue}` : null;
    });

    const filteredDetails = details.filter((detail) => detail !== null);

    if (filteredDetails.length > 0) {
      appendDefinitionList(container, div, filteredDetails);
    }
  }
}

function isValueEmpty(value) {
  return value === undefined || value === null || value === "";
}

function mapArrayValues(question, values) {
  return values
    .map((value) => {
      if (isPlainChoiceObject(value)) {
        return value.text || value.title || value.value;
      }
      const display = formatPrimitiveDisplayValue(question, value);
      return isValueEmpty(display) ? null : display;
    })
    .filter((value) => !isValueEmpty(value));
}

function formatPrimitiveDisplayValue(question, value) {
  if (value === undefined || value === null) {
    return "";
  }

  if (isPlainChoiceObject(value)) {
    return value.text || value.title || value.value;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  const choiceText = getChoiceText(question, value);
  if (choiceText) {
    return choiceText;
  }

  if (typeof value === "number") {
    return value.toString();
  }

  if (typeof value === "string") {
    if (value.trim() === "") {
      return "";
    }
    return value.replace(/_/g, " ");
  }

  return value;
}

function getChoiceText(question, value) {
  if (!question || value === undefined || value === null) {
    return null;
  }

  const normalizedValue = value?.value ?? value;
  const choices = question.visibleChoices || question.choices || [];

  const normalizedComparableValue =
    typeof normalizedValue === "string" ? normalizedValue.toLowerCase() : normalizedValue;

  const normalizedBooleanLikeValue = parseBooleanLike(normalizedComparableValue);

  const match = choices.find((choice) => {
    const choiceValue = choice?.value ?? choice;
    const normalizedChoiceValue =
      typeof choiceValue === "string" ? choiceValue.toLowerCase() : choiceValue;

    if (normalizedComparableValue === normalizedChoiceValue) {
      return true;
    }

    if (typeof normalizedValue === "boolean" || normalizedBooleanLikeValue !== null) {
      const effectiveBooleanValue =
        typeof normalizedValue === "boolean" ? normalizedValue : normalizedBooleanLikeValue;
      const truthyStringValues = ["yes", "true", "1"];
      const falsyStringValues = ["no", "false", "0"];

      if (effectiveBooleanValue && truthyStringValues.includes(normalizedChoiceValue)) {
        return true;
      }

      if (!effectiveBooleanValue && falsyStringValues.includes(normalizedChoiceValue)) {
        return true;
      }
    }

    return false;
  });

  if (match) {
    return match.text || match.title || match.value;
  }

  if (typeof normalizedValue === "boolean" || normalizedBooleanLikeValue !== null) {
    const effectiveBooleanValue =
      typeof normalizedValue === "boolean" ? normalizedValue : normalizedBooleanLikeValue;

    if (question.getType?.() === "boolean" || question.cellType === "boolean") {
      return effectiveBooleanValue ? "Yes" : "No";
    }
  }

  if (
    typeof normalizedValue === "string" &&
    question.otherItem &&
    normalizedValue === question.otherItem.value
  ) {
    return question.otherText || normalizedValue;
  }

  return null;
}

function parseBooleanLike(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    if (["yes", "true", "1"].includes(value)) {
      return true;
    }

    if (["no", "false", "0"].includes(value)) {
      return false;
    }
  }

  return null;
}

function isPlainChoiceObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const keys = Object.keys(value);
  return keys.every((key) => ["value", "text", "title"].includes(key));
}
